import {
  getGetNotificationHubInsightsOutboxHistoryMockHandler,
  getGetNotificationHubInsightsSummaryMockHandler,
} from "@vega-api/generated/notification-hub-v1/notification-hub-v1.msw";
import { type ApiResponseInsightsOutboxHistory } from "@vega-api/generated/types/apiResponseInsightsOutboxHistory";
import { type ApiResponseInsightsSummary } from "@vega-api/generated/types/apiResponseInsightsSummary";
import { type EmailInsightsGranularity } from "@vega-api/generated/types/emailInsightsGranularity";
import { type EmailInsightsRange } from "@vega-api/generated/types/emailInsightsRange";
import { type InsightsOutboxChunk } from "@vega-api/generated/types/insightsOutboxChunk";

/**
 * Custom MSW handlers for the Notification Hub Insights endpoints.
 *
 * Orval's global mock config strips computed fields, so these wrap the generated mock
 * factories with realistic, range-consistent aggregates: the summary totals are derived from
 * the same chunks the outbox-history endpoint returns, anchored to a fixed UTC "now" so the
 * windows, labels, and counts are deterministic (stable snapshots/tests).
 */

// Frozen anchor — a fixed literal, deliberately NOT `new Date()`/`Date.now()`, so every
// window, label, and count is byte-stable across runs (deterministic snapshots/tests).
const NOW = new Date("2026-06-12T12:00:00Z");

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Deterministic pseudo-random in [0, 1) from an integer seed — keeps mock data stable.
const seeded = (index: number): number => {
  const value = Math.sin((index + 1) * 12.9898) * 43758.5453;

  return value - Math.floor(value);
};

const startOfUtcDay = (date: Date): Date =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );

const mondayOfUtcWeek = (date: Date): Date => {
  const day = startOfUtcDay(date);
  const dow = day.getUTCDay(); // 0 = Sunday … 6 = Saturday
  const backToMonday = (dow + 6) % 7;

  return new Date(day.getTime() - backToMonday * MS_PER_DAY);
};

const startOfUtcMonth = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

const addUtcMonths = (date: Date, months: number): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));

const dayLabel = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);

const monthLabel = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);

type ChunkBounds = { start: Date; end: Date; label: string };

const chunkBoundsForRange = (range: EmailInsightsRange): ChunkBounds[] => {
  if (range === "15d") {
    const bounds: ChunkBounds[] = [];
    const firstDay = new Date(startOfUtcDay(NOW).getTime() - 14 * MS_PER_DAY);

    for (let i = 0; i < 15; i++) {
      const start = new Date(firstDay.getTime() + i * MS_PER_DAY);
      const nextDay = new Date(start.getTime() + MS_PER_DAY);
      const end = i === 14 ? NOW : nextDay; // newest day is partial

      bounds.push({ start, end, label: dayLabel(start) });
    }

    return bounds;
  }

  if (range === "3m") {
    const bounds: ChunkBounds[] = [];
    const currentMonday = mondayOfUtcWeek(NOW);
    // 13 full Mon–Sun weeks + the partial current week = 14 chunks.
    const firstMonday = new Date(currentMonday.getTime() - 13 * 7 * MS_PER_DAY);

    for (let i = 0; i < 14; i++) {
      const start = new Date(firstMonday.getTime() + i * 7 * MS_PER_DAY);
      const nextMonday = new Date(start.getTime() + 7 * MS_PER_DAY);
      const end = i === 13 ? NOW : nextMonday; // current week is partial
      const lastDay = new Date(nextMonday.getTime() - MS_PER_DAY);

      bounds.push({
        start,
        end,
        label: `${dayLabel(start)} – ${dayLabel(lastDay)}`,
      });
    }

    return bounds;
  }

  // 12m → 12 calendar months, current month partial.
  const bounds: ChunkBounds[] = [];
  const firstMonth = addUtcMonths(startOfUtcMonth(NOW), -11);

  for (let i = 0; i < 12; i++) {
    const start = addUtcMonths(firstMonth, i);
    const nextMonth = addUtcMonths(firstMonth, i + 1);
    const end = i === 11 ? NOW : nextMonth; // current month is partial

    bounds.push({ start, end, label: monthLabel(start) });
  }

  return bounds;
};

const granularityForRange = (
  range: EmailInsightsRange,
): EmailInsightsGranularity => {
  if (range === "15d") return "DAY";

  if (range === "3m") return "WEEK";

  return "MONTH";
};

// Per-chunk base volume, tuned so totals land near the design's example figures.
const baseVolumeForRange = (range: EmailInsightsRange): number => {
  if (range === "15d") return 600;

  if (range === "3m") return 4200;

  return 18000;
};

// Distinct over-messaged recipients (>3 emails/day on average); the threshold scales with the
// window, so the count grows with the range.
const spamWarningsForRange = (range: EmailInsightsRange): number => {
  if (range === "15d") return 7;

  if (range === "3m") return 19;

  return 64;
};

const buildChunks = (range: EmailInsightsRange): InsightsOutboxChunk[] => {
  const bounds = chunkBoundsForRange(range);
  const base = baseVolumeForRange(range);
  const lastIndex = bounds.length - 1;

  return bounds.map(({ start, end, label }, index) => {
    const variation = 0.7 + seeded(index) * 0.6; // 0.7–1.3
    const partialFactor = index === lastIndex ? 0.45 : 1; // newest chunk is partial
    const total = Math.round(base * variation * partialFactor);

    const blockedBounced = Math.round(
      total * (0.03 + seeded(index + 50) * 0.04),
    );
    const opened = Math.round(
      (total - blockedBounced) * (0.55 + seeded(index + 99) * 0.18),
    );
    const receivedUnopened = total - blockedBounced - opened;

    return {
      chunkStart: start.toISOString(),
      chunkEnd: end.toISOString(),
      label,
      opened,
      receivedUnopened,
      blockedBounced,
      total,
    };
  });
};

const buildOutboxResponse = (
  range: EmailInsightsRange,
): ApiResponseInsightsOutboxHistory => ({
  data: {
    range,
    granularity: granularityForRange(range),
    chunks: buildChunks(range),
  },
  errors: null,
});

const buildSummaryResponse = (
  range: EmailInsightsRange,
): ApiResponseInsightsSummary => {
  const chunks = buildChunks(range);
  const sentTotal = chunks.reduce((sum, chunk) => sum + chunk.total, 0);
  const opened = chunks.reduce((sum, chunk) => sum + chunk.opened, 0);
  const receivedUnopened = chunks.reduce(
    (sum, chunk) => sum + chunk.receivedUnopened,
    0,
  );
  const blockedBounced = chunks.reduce(
    (sum, chunk) => sum + chunk.blockedBounced,
    0,
  );

  return {
    data: {
      range,
      windowStart: chunks[0].chunkStart,
      windowEnd: chunks[chunks.length - 1].chunkEnd,
      sentTotal,
      opened,
      receivedUnopened,
      blockedBounced,
      // Distinct recipients affected — fewer than the email count in the red bucket.
      bounceWarnings: Math.round(blockedBounced * 0.62),
      spamWarnings: spamWarningsForRange(range),
      mostUsedTemplate: {
        templateName: "APOLLO_CAPITAL_CALL_NOTICE_TEMPLATE",
        templateDescription: "Capital call notice",
        sentCount: Math.round(sentTotal * 0.34),
      },
    },
    errors: null,
  };
};

const parseRange = (request: Request): EmailInsightsRange => {
  const value = new URL(request.url).searchParams.get("range");

  return value === "3m" || value === "12m" ? value : "15d";
};

export const notificationHubInsightsHandlers = [
  getGetNotificationHubInsightsSummaryMockHandler((info) =>
    buildSummaryResponse(parseRange(info.request)),
  ),
  getGetNotificationHubInsightsOutboxHistoryMockHandler((info) =>
    buildOutboxResponse(parseRange(info.request)),
  ),
];
